import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, forkJoin, switchMap } from 'rxjs';

import { ApiService } from '../../core/api.service';
import { I18nService } from '../../core/i18n.service';
import { ItemMaster, OFESEstablishment, PageResult } from '../../core/models';
import { PaginationComponent } from '../../core/pagination.component';
import { TranslatePipe } from '../../core/translate.pipe';

interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox' | 'file' | 'items' | 'tender-items' | 'sample-items' | 'item-select' | 'budget-head' | 'sender-group';
  required?: boolean;
  options?: string[];
  optionItems?: SelectOption[];
  lookup?: 'budget-heads' | 'establishments';
  referenceStage?: string;
  referenceMode?: string;
  referenceLabelKeys?: string[];
  referenceOptions?: ReferenceOption[];
  readonly?: boolean;
  hidden?: boolean;
  displayOnly?: boolean;
  wide?: boolean;
  span?: 3 | 4 | 5 | 6 | 12 | 15;
}

interface ReferenceOption {
  value: number | string;
  label: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const statuses = ['draft', 'submitted', 'in_progress', 'approved', 'rejected', 'completed', 'cancelled'];
const approvalStatuses = ['approved', 'rejected'];
const procurementModes = ['GEM', 'E_TENDER', 'GLOBAL_TENDER', 'LPC', 'OPEN_MARKET', 'Others'];
const procurementRegisterStatuses = ['In Tendering Process', 'Closed', 'Re-Tendered', 'Cancelled'];
const gemProcurementTypes = [
  'Product / Normal Category Bid',
  'Custom Catalogue Bid',
  'BOQ (Bill of Quantities) Bid',
  'Service Bid',
  'Bid to RA (Reverse Auction)'
];
const gemDeliveryStatuses = ['Delivered', 'In Transit', 'Not Delivered', 'Delayed'];
const OTHER_OPTION = 'Others';
const REQUIRED_TEXT: ValidatorFn = (control) =>
  typeof control.value === 'string' && !control.value.trim() ? { required: true } : null;
const HIDDEN_STAGES = new Set(['demands', 'item-database']);
const PROCESS_STAGES = new Set([
  'tenders',
  'emds',
  'samples',
  'technical-evaluations',
  'financial-evaluations',
  'purchase-orders',
  'performance-securities',
  'delivery-acceptance',
  'payments'
]);
const STAGE_LABELS: Record<string, string> = {
  requisitions: 'Requisition Register',
  'procurement-cases': 'Procurement Case Register',
  'gem-cases': 'GeM Procurement',
  'e-tender-cases': 'e-Tendering',
  'global-tender-cases': 'Global Tender',
  'lpc-cases': 'Local Purchase Committee',
  'open-market-cases': 'Open Market Procurement',
  'other-procurement-cases': 'Other Procurement',
  tenders: 'Tender/Bid Register',
  emds: 'EMD Tracker/Register',
  samples: 'Sample Register',
  'technical-evaluations': 'Technical Evaluation',
  'financial-evaluations': 'Financial Evaluation',
  'purchase-orders': 'Purchase Order',
  'performance-securities': 'Performance Security',
  'delivery-acceptance': 'Delivery/Acceptance',
  payments: 'Payment Tracker'
};

const STAGE_FIELDS: Record<string, FieldDef[]> = {
  demands: [
    { key: 'demand_no', label: 'Demand No', type: 'text', required: true },
    { key: 'department', label: 'Department', type: 'text', required: true },
    { key: 'item_description', label: 'Item Description', type: 'item-select', required: true },
    { key: 'quantity', label: 'Quantity', type: 'number', required: true },
    { key: 'estimated_value', label: 'Estimated Value', type: 'number' },
    { key: 'required_by', label: 'Required By', type: 'date' },
    { key: 'status', label: 'Status', type: 'select', options: statuses }
  ],
  requisitions: [
    { key: 'requisition_no', label: 'Letter No. / Requisition No.', type: 'text', required: true, span: 5 },
    { key: 'requisition_date', label: 'Date', type: 'date', required: true, span: 5 },
    { key: 'requisition_received_from_group', label: 'Requisition Received From', type: 'sender-group', displayOnly: true, span: 15 },
    { key: 'sender_designation', label: 'Designation', type: 'text', hidden: true },
    { key: 'received_from', label: 'Fire Station / Establishment Name', type: 'select', options: [], lookup: 'establishments', hidden: true },
    { key: 'received_from_district', label: 'District', type: 'text', readonly: true, hidden: true },
    { key: 'received_from_range', label: 'Range', type: 'text', readonly: true, hidden: true },
    { key: 'requisition_items', label: 'Items', type: 'items', required: true, span: 15 },
    { key: 'budget_head', label: 'Budget Head', type: 'budget-head', options: [], lookup: 'budget-heads', span: 5 },
    { key: 'administrative_approval_quantity', label: 'Administrative Approval Quantity', type: 'number', span: 5 },
    { key: 'administrative_approval_date', label: 'Administrative Approval Date', type: 'date', span: 5 },
    { key: 'approval_status', label: 'Approval Status', type: 'select', options: approvalStatuses, span: 5 },
    { key: 'procurement_mode', label: 'Mode of Procurement', type: 'select', options: procurementModes, span: 5 }
  ],
  'procurement-cases': [
    { key: 'procurement_case_no', label: 'Procurement Case No', type: 'text', required: true, span: 3 },
    { key: 'requisition_id', label: 'Linked Requisition', type: 'select', referenceStage: 'requisitions', referenceLabelKeys: ['requisition_no', 'received_from'], span: 3 },
    { key: 'approved_quantity', label: 'Approved Quantity', type: 'number', span: 3 },
    { key: 'approved_estimated_cost', label: 'Approved Estimated Cost', type: 'number', span: 3 },
    { key: 'procurement_mode', label: 'Procurement Mode', type: 'select', options: procurementModes, required: true, span: 3 },
    { key: 'case_status', label: 'Status', type: 'select', options: procurementRegisterStatuses, span: 3 },
    { key: 'mode_selection_date', label: 'Mode Selection Date', type: 'date', span: 3 },
    { key: 'mode_selected_by', label: 'Mode Selected By', type: 'text', span: 3 },
    { key: 'emd_applicable', label: 'EMD Applicable', type: 'checkbox', span: 3 },
    { key: 'pbg_applicable', label: 'PBG Applicable', type: 'checkbox', span: 3 },
    { key: 'file_no', label: 'File No', type: 'text', span: 3 },
    { key: 'sanction_no', label: 'Sanction No', type: 'text', span: 3 },
    { key: 'sanction_date', label: 'Sanction Date', type: 'date', span: 3 },
    { key: 'approving_authority', label: 'Approving Authority', type: 'text', span: 3 },
    { key: 'remarks', label: 'Remarks', type: 'textarea', span: 12 }
  ],
  'gem-cases': [
    { key: 'procurement_case_id', label: 'Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceMode: 'GEM', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'gem_procurement_type', label: 'GeM Procurement Type', type: 'select', options: gemProcurementTypes, span: 3 },
    { key: 'gem_bid_no', label: 'GeM Bid No', type: 'text', span: 3 },
    { key: 'gem_bid_date', label: 'GeM Bid Date', type: 'date', span: 3 },
    { key: 'gem_contract_no', label: 'GeM Contract No', type: 'text', span: 3 },
    { key: 'gem_contract_date', label: 'GeM Contract Date', type: 'date', span: 3 },
    { key: 'gem_order_value', label: 'GeM Order Value', type: 'number', span: 3 },
    { key: 'vendor_id', label: 'Vendor ID', type: 'number', span: 3 },
    { key: 'delivery_status', label: 'Delivery Status', type: 'select', options: gemDeliveryStatuses, span: 3 },
    { key: 'delivery_delay_reason', label: 'Delay Reason', type: 'textarea', span: 12 },
    { key: 'delivery_delay_document_path', label: 'Delay Supporting Document', type: 'file', span: 6 },
    { key: 'payment_status', label: 'Payment Status', type: 'text', span: 3 },
    { key: 'status', label: 'Status', type: 'select', options: procurementRegisterStatuses, span: 3 }
  ],
  'e-tender-cases': [
    { key: 'procurement_case_id', label: 'Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceMode: 'E_TENDER', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'tender_ref_no', label: 'Tender Reference No', type: 'text', required: true, span: 3 },
    { key: 'tender_publication_date', label: 'Tender Publication Date', type: 'date', span: 3 },
    { key: 'pre_bid_meeting_date', label: 'Pre-Bid Meeting Date', type: 'date', span: 3 },
    { key: 'bid_submission_end_date', label: 'Bid Submission End Date', type: 'date', span: 3 },
    { key: 'technical_bid_opening_date', label: 'Technical Bid Opening Date', type: 'date', span: 3 },
    { key: 'financial_bid_opening_date', label: 'Financial Bid Opening Date', type: 'date', span: 3 },
    { key: 'estimated_cost', label: 'Estimated Cost', type: 'number', span: 3 },
    { key: 'technical_evaluation_status', label: 'Technical Evaluation Status', type: 'text', span: 3 },
    { key: 'financial_evaluation_status', label: 'Financial Evaluation Status', type: 'text', span: 3 },
    { key: 'l1_vendor_id', label: 'L1 Vendor ID', type: 'number', span: 3 },
    { key: 'award_approval_date', label: 'Award Approval Date', type: 'date', span: 3 },
    { key: 'loa_no', label: 'LOA No', type: 'text', span: 3 },
    { key: 'loa_date', label: 'LOA Date', type: 'date', span: 3 },
    { key: 'contract_no', label: 'Contract No', type: 'text', span: 3 },
    { key: 'status', label: 'Status', type: 'select', options: procurementRegisterStatuses, span: 3 }
  ],
  'global-tender-cases': [
    { key: 'procurement_case_id', label: 'Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceMode: 'GLOBAL_TENDER', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'global_tender_ref_no', label: 'Global Tender Ref No', type: 'text', required: true, span: 3 },
    { key: 'country_of_vendor', label: 'Country of Vendor', type: 'text', span: 3 },
    { key: 'foreign_vendor_name', label: 'Foreign Vendor Name', type: 'text', span: 3 },
    { key: 'foreign_currency', label: 'Foreign Currency', type: 'text', span: 3 },
    { key: 'exchange_rate', label: 'Exchange Rate', type: 'number', span: 3 },
    { key: 'bid_value_foreign_currency', label: 'Bid Value Foreign Currency', type: 'number', span: 3 },
    { key: 'bid_value_inr', label: 'Bid Value INR', type: 'number', span: 3 },
    { key: 'technical_evaluation_status', label: 'Technical Evaluation Status', type: 'text', span: 3 },
    { key: 'commercial_evaluation_status', label: 'Commercial Evaluation Status', type: 'text', span: 3 },
    { key: 'approval_date', label: 'Approval Date', type: 'date', span: 3 },
    { key: 'contract_no', label: 'Contract No', type: 'text', span: 3 },
    { key: 'status', label: 'Status', type: 'select', options: procurementRegisterStatuses, span: 3 }
  ],
  'lpc-cases': [
    { key: 'procurement_case_id', label: 'Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceMode: 'LPC', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'lpc_meeting_no', label: 'LPC Meeting No', type: 'text', span: 3 },
    { key: 'lpc_meeting_date', label: 'LPC Meeting Date', type: 'date', span: 3 },
    { key: 'committee_members', label: 'Committee Members', type: 'textarea', span: 12 },
    { key: 'quotation_count', label: 'Quotation Count', type: 'number', span: 3 },
    { key: 'lowest_vendor_id', label: 'Lowest Vendor ID', type: 'number', span: 3 },
    { key: 'comparative_statement_path', label: 'Comparative Statement Path', type: 'text', span: 6 },
    { key: 'recommendation_date', label: 'Recommendation Date', type: 'date', span: 3 },
    { key: 'approval_date', label: 'Approval Date', type: 'date', span: 3 },
    { key: 'status', label: 'Status', type: 'select', options: procurementRegisterStatuses, span: 3 }
  ],
  'open-market-cases': [
    { key: 'procurement_case_id', label: 'Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceMode: 'OPEN_MARKET', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'procurement_justification', label: 'Procurement Justification', type: 'textarea', span: 12 },
    { key: 'market_survey_date', label: 'Market Survey Date', type: 'date', span: 3 },
    { key: 'quotation_count', label: 'Quotation Count', type: 'number', span: 3 },
    { key: 'selected_vendor_id', label: 'Selected Vendor ID', type: 'number', span: 3 },
    { key: 'comparative_statement_path', label: 'Comparative Statement Path', type: 'text', span: 6 },
    { key: 'approval_date', label: 'Approval Date', type: 'date', span: 3 },
    { key: 'status', label: 'Status', type: 'select', options: procurementRegisterStatuses, span: 3 }
  ],
  'other-procurement-cases': [
    { key: 'procurement_case_id', label: 'Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceMode: 'OTHER', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'other_procurement_type', label: 'Other Procurement Type', type: 'text', span: 3 },
    { key: 'procurement_reference_no', label: 'Procurement Reference No', type: 'text', span: 3 },
    { key: 'vendor_id', label: 'Vendor ID', type: 'number', span: 3 },
    { key: 'approval_date', label: 'Approval Date', type: 'date', span: 3 },
    { key: 'status', label: 'Status', type: 'select', options: procurementRegisterStatuses, span: 3 },
    { key: 'procurement_description', label: 'Procurement Description', type: 'textarea', span: 12 }
  ],
  tenders: [
    { key: 'serial_no', label: 'Serial No.', type: 'number', span: 3 },
    { key: 'tender_no', label: 'Tender Number', type: 'text', required: true, span: 3 },
    { key: 'tender_date', label: 'Date', type: 'date', span: 3 },
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'requisition_id', label: 'Source Requisition', type: 'select', referenceStage: 'requisitions', referenceLabelKeys: ['requisition_no', 'received_from'], hidden: true },
    { key: 'tender_items', label: 'Name of the Items', type: 'tender-items', span: 12 },
    { key: 'quantity', label: 'Quantity', type: 'number', span: 3 },
    { key: 'estimated_cost', label: 'Estimated Cost', type: 'number', span: 3 },
    { key: 'budget_head', label: 'Budget Head', type: 'budget-head', options: [], lookup: 'budget-heads', span: 6 },
    { key: 'bid_published_date', label: 'Bid Published Date', type: 'date', span: 3 },
    { key: 'bid_closed_date', label: 'Bid Closed Date', type: 'date', span: 3 },
    { key: 'technical_bid_opening_date', label: 'Technical Bid Opening Date', type: 'date', span: 3 },
    { key: 'technical_qualified_bidders', label: 'No. of Technically Qualified Bidders', type: 'number', span: 3 },
    { key: 'product_demo_date', label: 'Product Demo Date', type: 'date', span: 3 },
    { key: 'technical_evaluation_date', label: 'Technical Evaluation Date', type: 'date', span: 3 },
    { key: 'financial_bid_opening_date', label: 'Financial Bid Opening Date', type: 'date', span: 3 },
    { key: 'l1_bidder_name', label: 'L1 Bidder Name (After RA/Negotiation)', type: 'text', span: 3 },
    { key: 'contract_number', label: 'Contract Number', type: 'text', span: 3 },
    { key: 'contract_date', label: 'Contract Date', type: 'date', span: 3 },
    { key: 'amount', label: 'Amount', type: 'number', span: 3 },
    { key: 'remarks', label: 'Remarks', type: 'textarea', span: 12 }
  ],
  emds: [
    { key: 'serial_no', label: 'Sl. No.', type: 'number', span: 3 },
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'tender_id', label: 'Tender', type: 'select', referenceStage: 'tenders', referenceLabelKeys: ['tender_no', 'title'], hidden: true },
    { key: 'emd_unique_id', label: 'EMD Unique ID', type: 'text', span: 3 },
    { key: 'entry_date', label: 'Date', type: 'date', span: 3 },
    { key: 'tender_number', label: 'Tender Number', type: 'text', span: 3 },
    { key: 'tender_date', label: 'Tender Date', type: 'date', span: 3 },
    { key: 'item_name', label: 'Name of the Items', type: 'item-select', span: 6 },
    { key: 'bidder_name', label: 'Name of the Bidder', type: 'text', required: true, span: 3 },
    { key: 'emd_form', label: 'Form of EMD (BG/FDR)', type: 'text', span: 3 },
    { key: 'emd_no', label: 'EMD No.', type: 'text', span: 3 },
    { key: 'emd_date', label: 'EMD Date', type: 'date', span: 3 },
    { key: 'amount', label: 'EMD Amount', type: 'number', span: 3 },
    { key: 'issuing_branch', label: 'Issuing Branch', type: 'text', span: 3 },
    { key: 'bank_confirmation_date', label: 'Bank Confirmation Date', type: 'date', span: 3 },
    { key: 'expiry_date', label: 'Expiry Date', type: 'date', span: 3 },
    { key: 'returned_date', label: 'Returned Date', type: 'date', span: 3 },
    { key: 'return_details', label: 'Return Details', type: 'textarea', span: 6 },
    { key: 'remarks', label: 'Remarks', type: 'textarea', span: 6 }
  ],
  samples: [
    { key: 'serial_no', label: 'Serial No.', type: 'number', span: 3 },
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'], span: 3 },
    { key: 'tender_id', label: 'Tender', type: 'select', referenceStage: 'tenders', referenceLabelKeys: ['tender_no', 'title'], hidden: true },
    { key: 'tender_number', label: 'Tender Number', type: 'text', span: 3 },
    { key: 'tender_date', label: 'Tender Date', type: 'date', span: 3 },
    { key: 'demonstration_date', label: 'Demonstration Date', type: 'date', span: 3 },
    { key: 'sample_items', label: 'Items / Quantity / Make / Model', type: 'sample-items', span: 12 },
    { key: 'bidder_name', label: 'Name of the Bidder', type: 'text', required: true, span: 4 },
    { key: 'receipt_date', label: 'Receipt Date', type: 'date', span: 4 },
    { key: 'recipient_name', label: 'Name of the Recipient', type: 'text', span: 4 },
    { key: 'remarks', label: 'Remarks', type: 'textarea', span: 12 }
  ],
  'technical-evaluations': [
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'] },
    { key: 'tender_id', label: 'Tender', type: 'select', referenceStage: 'tenders', referenceLabelKeys: ['tender_no', 'title'], hidden: true },
    { key: 'bidder_name', label: 'Bidder Name', type: 'text', required: true },
    { key: 'evaluator', label: 'Evaluator', type: 'text', required: true },
    { key: 'score', label: 'Score', type: 'number' },
    { key: 'result', label: 'Result', type: 'text' },
    { key: 'remarks', label: 'Remarks', type: 'textarea' }
  ],
  'financial-evaluations': [
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'] },
    { key: 'tender_id', label: 'Tender', type: 'select', referenceStage: 'tenders', referenceLabelKeys: ['tender_no', 'title'], hidden: true },
    { key: 'bidder_name', label: 'Bidder Name', type: 'text', required: true },
    { key: 'quoted_amount', label: 'Quoted Amount', type: 'number', required: true },
    { key: 'negotiated_amount', label: 'Negotiated Amount', type: 'number' },
    { key: 'rank', label: 'Rank', type: 'number' },
    { key: 'result', label: 'Result', type: 'text' }
  ],
  'purchase-orders': [
    { key: 'po_no', label: 'PO No', type: 'text', required: true },
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'] },
    { key: 'tender_id', label: 'Tender', type: 'select', referenceStage: 'tenders', referenceLabelKeys: ['tender_no', 'title'], hidden: true },
    { key: 'po_date', label: 'Order Date', type: 'date' },
    { key: 'procurement_mode', label: 'Mode of Procurement', type: 'select', options: procurementModes },
    { key: 'tender_reference_no', label: 'Tender Reference No', type: 'text' },
    { key: 'tender_reference_date', label: 'Tender Reference Date', type: 'date' },
    { key: 'estimated_cost', label: 'Estimated Cost', type: 'number' },
    { key: 'process_initiate_date', label: 'Process Initiate Date', type: 'date' },
    { key: 'budget_head', label: 'Budget Head', type: 'budget-head', options: [], lookup: 'budget-heads' },
    { key: 'vendor_name', label: 'Vendor Name', type: 'text', required: true },
    { key: 'item_description', label: 'Item Description', type: 'item-select', wide: true },
    { key: 'quantity', label: 'Quantity', type: 'number' },
    { key: 'unit', label: 'Unit', type: 'text' },
    { key: 'po_value', label: 'PO Value', type: 'number' },
    { key: 'status', label: 'Status', type: 'select', options: statuses }
  ],
  'performance-securities': [
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'] },
    { key: 'po_id', label: 'Purchase Order', type: 'select', referenceStage: 'purchase-orders', referenceLabelKeys: ['po_no', 'vendor_name'], hidden: true },
    { key: 'entry_date', label: 'Date', type: 'date' },
    { key: 'contract_number', label: 'Contract Number', type: 'text' },
    { key: 'contract_date', label: 'Contract Date', type: 'date' },
    { key: 'item_name', label: 'Name of the Items', type: 'item-select', wide: true },
    { key: 'quantity', label: 'Quantity', type: 'number' },
    { key: 'bidder_name', label: 'Bidder Name', type: 'text' },
    { key: 'security_form', label: 'Form of Performance Security (BG/FDR)', type: 'text' },
    { key: 'security_number', label: 'Performance Security Number', type: 'text' },
    { key: 'security_date', label: 'Performance Security Date', type: 'date' },
    { key: 'amount', label: 'Amount', type: 'number' },
    { key: 'issuing_branch', label: 'Issuing Branch', type: 'text' },
    { key: 'bank_confirmation_date', label: 'Bank Confirmation Date', type: 'date' },
    { key: 'expiry_date', label: 'Expiry Date', type: 'date' },
    { key: 'warranty_period', label: 'Warranty Period', type: 'text' },
    { key: 'return_date', label: 'Return Date', type: 'date' },
    { key: 'return_details', label: 'Return Details', type: 'textarea', wide: true },
    { key: 'remarks', label: 'Remarks', type: 'textarea', wide: true },
    { key: 'release_status', label: 'Release Status', type: 'text' }
  ],
  'delivery-acceptance': [
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'] },
    { key: 'po_id', label: 'Purchase Order', type: 'select', referenceStage: 'purchase-orders', referenceLabelKeys: ['po_no', 'vendor_name'], hidden: true },
    { key: 'delivery_date', label: 'Delivery Date', type: 'date' },
    { key: 'accepted_quantity', label: 'Accepted Quantity', type: 'number' },
    { key: 'acceptance_status', label: 'Acceptance Status', type: 'select', options: statuses },
    { key: 'remarks', label: 'Remarks', type: 'textarea' }
  ],
  payments: [
    { key: 'procurement_case_id', label: 'Linked Procurement Case', type: 'select', required: true, referenceStage: 'procurement-cases', referenceLabelKeys: ['procurement_case_no', 'procurement_mode'] },
    { key: 'po_id', label: 'Purchase Order', type: 'select', referenceStage: 'purchase-orders', referenceLabelKeys: ['po_no', 'vendor_name'], hidden: true },
    { key: 'invoice_no', label: 'Invoice No', type: 'text', required: true },
    { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
    { key: 'invoice_amount', label: 'Invoice Amount', type: 'number', required: true },
    { key: 'paid_amount', label: 'Paid Amount', type: 'number' },
    { key: 'payment_status', label: 'Payment Status', type: 'text' }
  ]
};

@Component({
  selector: 'app-lifecycle',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, ReactiveFormsModule, TitleCasePipe, TranslatePipe],
  template: `
    <section class="page-header">
      <div>
        <h1>{{ stageLabel | t }}</h1>
        <p>{{ (isItemDatabase ? 'lifecycle.itemDescription' : 'lifecycle.recordDescription') | t }}</p>
      </div>
      <button type="button" class="icon-button-text" (click)="resetForm()">
        <span class="button-icon" aria-hidden="true">+</span>
        {{ isItemDatabase ? ('common.addItem' | t) : ('common.addRecord' | t) }}
      </button>
    </section>

    <section class="panel">
      <form class="record-form" [class.requisition-form]="stage === 'requisitions'" [class.procurement-case-form]="stage === 'procurement-cases' || stage.endsWith('-cases')" [class.tender-register-form]="stage === 'tenders'" [class.emd-register-form]="stage === 'emds'" [class.sample-register-form]="stage === 'samples'" [formGroup]="form" (ngSubmit)="save()">
        <ng-container *ngFor="let field of fields">
          <div *ngIf="field.type === 'sender-group' && !field.hidden; else standardField" class="items-editor sender-editor" [ngClass]="field.span ? 'span-' + field.span : null">
            <div class="sender-title">{{ 'lifecycle.requisitionReceivedFrom' | t }}</div>
            <div class="sender-grid">
              <label>
                <span class="field-label">{{ 'Designation' | t }}</span>
                <input type="text" formControlName="sender_designation">
              </label>
              <label>
                <span class="field-label">{{ 'Fire Station / Establishment Name' | t }}</span>
                <input type="text" formControlName="received_from" list="establishment-options" [placeholder]="'common.typeOrSelectEstablishment' | t" (change)="syncEstablishmentDetails()" (blur)="syncEstablishmentDetails()">
                <datalist id="establishment-options">
                  <option *ngFor="let option of establishmentOptions" [value]="option.value"></option>
                </datalist>
              </label>
              <label>
                <span class="field-label">{{ 'Range' | t }}</span>
                <input type="text" formControlName="received_from_range" readonly>
              </label>
            </div>
          </div>
          <ng-template #standardField>
            <label *ngIf="!field.hidden" [class.wide]="field.type === 'textarea' || field.wide" [ngClass]="field.span ? 'span-' + field.span : null">
              <span class="field-label">
                {{ field.label | t }}<span class="required-marker" *ngIf="field.required" aria-hidden="true">*</span>
              </span>
              <ng-container [ngSwitch]="field.type">
                <textarea *ngSwitchCase="'textarea'" rows="3" [formControlName]="field.key"></textarea>
                <div *ngSwitchCase="'items'" class="items-editor" formArrayName="requisition_items">
                  <div class="sender-title">{{ 'Item Table' | t }}</div>
                  <div class="requisition-table-scroll">
                    <table class="data-table requisition-item-table">
                      <thead>
                        <tr>
                          <th>{{ 'Sr. No.' | t }}</th>
                          <th>{{ 'Name of the Item' | t }}</th>
                          <th>{{ 'Quantity Required' | t }}</th>
                          <th>{{ 'Estimated Cost' | t }}</th>
                          <th>{{ 'Quantity Available at Station/Store' | t }}</th>
                          <th>{{ 'Present Condition' | t }}</th>
                          <th>{{ 'Condemnation Status' | t }}</th>
                          <th>{{ 'Condemnation Date/Year' | t }}</th>
                          <th>{{ 'Action' | t }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <ng-container *ngFor="let item of requisitionItems.controls; let i = index">
                          <tr class="item-primary-row" [formGroupName]="i">
                            <td><input type="number" min="1" formControlName="serial_no" [attr.aria-label]="'Sr. No.' | t"></td>
                            <td class="item-name-cell">
                              <select formControlName="description" (change)="onItemDescriptionChange(i)" [attr.aria-label]="'Name of the Item' | t">
                                <option value="">{{ 'common.selectItem' | t }}</option>
                                <option *ngFor="let option of itemOptions" [value]="option.name">{{ itemOptionLabel(option) }}</option>
                                <option [value]="otherOption">{{ otherOption | t }}</option>
                                <option *ngIf="isUnlistedItemDescription(item.get('description')?.value)" [value]="item.get('description')?.value">{{ item.get('description')?.value }}</option>
                              </select>
                              <input *ngIf="isOtherItemDescription(i)" type="text" formControlName="description_other" [placeholder]="'Enter item description' | t">
                              <small class="field-error" *ngIf="itemControlInvalid(i, 'description')">{{ itemValidationMessage(i, 'description', 'Name of the Item') }}</small>
                              <small class="field-error" *ngIf="itemControlInvalid(i, 'description_other')">{{ itemValidationMessage(i, 'description_other', 'Name of the Item') }}</small>
                            </td>
                            <td><input type="number" min="0" formControlName="quantity_required" [attr.aria-label]="'Quantity Required' | t"></td>
                            <td>
                              <input type="number" min="0" step="0.01" formControlName="estimated_cost" [attr.aria-label]="'Estimated Cost' | t">
                              <small class="field-error" *ngIf="itemControlInvalid(i, 'estimated_cost')">{{ itemValidationMessage(i, 'estimated_cost', 'Estimated Cost') }}</small>
                            </td>
                            <td><input type="number" min="0" formControlName="quantity_available" [attr.aria-label]="'Quantity Available at Station/Store' | t"></td>
                            <td>
                              <select formControlName="present_condition" (change)="onPresentConditionChange(i)" [attr.aria-label]="'Present Condition' | t">
                                <option value="">{{ 'common.select' | t }}</option>
                                <option value="Serviceable">{{ 'Serviceable' | t }}</option>
                                <option value="Unserviceable">{{ 'Unserviceable' | t }}</option>
                              </select>
                            </td>
                            <td>
                              <select formControlName="condemnation_status" [attr.aria-label]="'Condemnation Status' | t">
                                <option value="">{{ 'common.select' | t }}</option>
                                <option value="Condemnable">{{ 'Condemnable' | t }}</option>
                                <option value="Not Condemnable">{{ 'Not Condemnable' | t }}</option>
                              </select>
                            </td>
                            <td><input type="text" formControlName="condemnation_date_year" placeholder="YYYY or DD-MM-YYYY" [attr.aria-label]="'Condemnation Date/Year' | t"></td>
                            <td><button type="button" class="secondary" (click)="removeItem(i)" [disabled]="requisitionItems.length === 1">{{ 'common.remove' | t }}</button></td>
                          </tr>
                          <tr class="item-notes-row" [formGroupName]="i">
                            <td colspan="9">
                              <div class="item-notes-grid">
                                <label>
                                  <span class="field-label">{{ 'Justification by Fire Station' | t }}</span>
                                  <textarea rows="3" formControlName="justification"></textarea>
                                </label>
                                <label>
                                  <span class="field-label">{{ 'Remarks' | t }}</span>
                                  <textarea rows="3" formControlName="remarks"></textarea>
                                </label>
                              </div>
                            </td>
                          </tr>
                        </ng-container>
                      </tbody>
                    </table>
                  </div>
                  <div class="item-export-actions">
                    <button type="button" class="secondary add-item" (click)="addItem()">{{ 'common.addItem' | t }}</button>
                    <button type="button" class="download excel" (click)="downloadRequisitionItems('xls')">{{ 'common.downloadExcel' | t }}</button>
                    <button type="button" class="secondary" (click)="downloadRequisitionItems('csv')">{{ 'Download CSV' | t }}</button>
                  </div>

                  <section class="unserviceable-section" *ngIf="hasUnserviceableItems">
                    <div class="sender-title">{{ 'If Unserviceable' | t }}</div>
                    <p class="unserviceable-help">{{ 'Complete the additional details for every unserviceable item.' | t }}</p>
                    <div class="requisition-table-scroll">
                      <table class="data-table unserviceable-item-table">
                        <thead>
                          <tr>
                            <th>{{ 'Sr. No.' | t }}</th>
                            <th>{{ 'Item Name' | t }}</th>
                            <th>{{ 'Date of Supply' | t }}</th>
                            <th>{{ 'Cause of Unserviceability' | t }}</th>
                            <th>{{ 'Remarks' | t }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <ng-container *ngFor="let item of requisitionItems.controls; let i = index">
                            <tr *ngIf="isUnserviceable(i)" [formGroupName]="i">
                              <td>{{ item.get('serial_no')?.value || i + 1 }}</td>
                              <td>{{ itemDisplayName(i) || '-' }}</td>
                              <td><input type="date" formControlName="date_of_supply" [attr.aria-label]="'Date of Supply' | t"></td>
                              <td>
                                <textarea rows="2" formControlName="cause_of_unserviceability" [attr.aria-label]="'Cause of Unserviceability' | t"></textarea>
                                <small class="field-error" *ngIf="itemControlInvalid(i, 'cause_of_unserviceability')">{{ itemValidationMessage(i, 'cause_of_unserviceability', 'Cause of Unserviceability') }}</small>
                              </td>
                              <td><textarea rows="2" formControlName="unserviceable_remarks" [attr.aria-label]="'Remarks' | t"></textarea></td>
                            </tr>
                          </ng-container>
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
                <div *ngSwitchCase="'tender-items'" class="items-editor" formArrayName="tender_items">
                  <div class="sender-title">{{ 'Tender Items' | t }}</div>
                  <div class="item-entry" *ngFor="let item of tenderItems.controls; let i = index" [formGroupName]="i">
                    <div class="tender-item-grid">
                      <label>
                        <span class="field-label">{{ 'Sl. No.' | t }}</span>
                        <input type="number" formControlName="serial_no">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Name of the Items' | t }}</span>
                        <input type="text" formControlName="description" list="item-master-options" [placeholder]="'common.typeOrSelectItem' | t" (change)="syncTenderItemType(i)" (blur)="syncTenderItemType(i)">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Type' | t }}</span>
                        <input type="text" formControlName="item_type">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Quantity' | t }}</span>
                        <input type="number" formControlName="quantity">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Unit' | t }}</span>
                        <input type="text" formControlName="unit">
                      </label>
                      <label class="item-action">
                        <span class="field-label">{{ 'Action' | t }}</span>
                        <button type="button" class="secondary" (click)="removeTenderItem(i)" [disabled]="tenderItems.length === 1">{{ 'common.remove' | t }}</button>
                      </label>
                    </div>
                  </div>
                  <datalist id="item-master-options">
                    <option *ngFor="let option of itemOptions" [value]="option.name">{{ itemOptionLabel(option) }}</option>
                  </datalist>
                  <button type="button" class="secondary add-item" (click)="addTenderItem()">{{ 'common.addItem' | t }}</button>
                </div>
                <div *ngSwitchCase="'sample-items'" class="items-editor" formArrayName="sample_items">
                  <div class="sender-title">{{ 'Sample Items' | t }}</div>
                  <div class="item-entry" *ngFor="let item of sampleItems.controls; let i = index" [formGroupName]="i">
                    <div class="sample-item-grid">
                      <label>
                        <span class="field-label">{{ 'Sl. No.' | t }}</span>
                        <input type="number" formControlName="serial_no">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Name of the Items' | t }}</span>
                        <input type="text" formControlName="description" list="item-master-options" [placeholder]="'common.typeOrSelectItem' | t">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Make' | t }}</span>
                        <input type="text" formControlName="make">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Model' | t }}</span>
                        <input type="text" formControlName="model">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Quantity' | t }}</span>
                        <input type="number" formControlName="quantity">
                      </label>
                      <label>
                        <span class="field-label">{{ 'Unit' | t }}</span>
                        <input type="text" formControlName="unit">
                      </label>
                      <label class="item-action">
                        <span class="field-label">{{ 'Action' | t }}</span>
                        <button type="button" class="secondary" (click)="removeSampleItem(i)" [disabled]="sampleItems.length === 1">{{ 'common.remove' | t }}</button>
                      </label>
                    </div>
                  </div>
                  <datalist id="item-master-options">
                    <option *ngFor="let option of itemOptions" [value]="option.name">{{ itemOptionLabel(option) }}</option>
                  </datalist>
                  <button type="button" class="secondary add-item" (click)="addSampleItem()">{{ 'common.addItem' | t }}</button>
                </div>
                <div *ngSwitchCase="'select'" class="select-with-other">
                  <select [formControlName]="field.key" (change)="handleSelectChange(field)">
                    <option value="">{{ 'common.select' | t }}</option>
                    <ng-container *ngIf="field.referenceStage; else staticOptions">
                      <option *ngFor="let option of field.referenceOptions || []" [ngValue]="option.value">{{ option.label }}</option>
                      <option *ngIf="isProcurementCaseReference(field)" [ngValue]="otherOption">{{ otherOption | t }}</option>
                    </ng-container>
                    <ng-template #staticOptions>
                      <ng-container *ngIf="field.optionItems?.length; else simpleOptions">
                        <option *ngFor="let option of field.optionItems" [value]="option.value">{{ option.label }}</option>
                      </ng-container>
                      <ng-template #simpleOptions>
                        <option *ngFor="let option of field.options" [value]="option">{{ option | t }}</option>
                      </ng-template>
                    </ng-template>
                  </select>
                  <div class="other-reference-field" *ngIf="showsOtherTextBox(field)">
                    <span class="other-field-label">{{ otherFieldLabel(field) | t }}<span class="required-marker" aria-hidden="true"> *</span></span>
                    <input type="text" [formControlName]="otherFieldKey(field)" [placeholder]="otherFieldPlaceholder(field) | t">
                    <small class="field-help">{{ 'Alphanumeric and special characters are allowed.' | t }}</small>
                    <small class="field-error" *ngIf="fieldControlInvalid(otherFieldKey(field))">{{ fieldValidationMessage(otherFieldKey(field), otherFieldLabel(field)) }}</small>
                  </div>
                </div>
                <div *ngSwitchCase="'budget-head'" class="budget-head-lookup">
                  <ng-container *ngIf="stage === 'requisitions' && field.key === 'budget_head'; else standardBudgetHead">
                    <select [formControlName]="field.key" (change)="onBudgetHeadChange()">
                      <option value="">{{ 'common.selectBudgetHead' | t }}</option>
                      <option *ngFor="let option of field.options" [value]="option">{{ option }}</option>
                      <option [value]="otherOption">{{ otherOption | t }}</option>
                      <option *ngIf="isUnlistedBudgetHead(field)" [value]="form.get(field.key)?.value">
                        {{ form.get(field.key)?.value }}
                      </option>
                    </select>
                    <div class="budget-head-other" *ngIf="isOtherBudgetHead">
                      <span>{{ 'Budget Head Name' | t }}<span class="required-marker" aria-hidden="true"> *</span></span>
                      <input type="text" formControlName="budget_head_other" [placeholder]="'Enter budget head name' | t">
                      <small class="field-error" *ngIf="fieldControlInvalid('budget_head_other')">{{ fieldValidationMessage('budget_head_other', 'Budget Head Name') }}</small>
                    </div>
                  </ng-container>
                  <ng-template #standardBudgetHead>
                    <textarea
                      rows="2"
                      [formControlName]="field.key"
                      [placeholder]="'common.typeOrSelectBudgetHead' | t"></textarea>
                    <div class="budget-head-options" *ngIf="field.options?.length">
                      <button
                        type="button"
                        class="budget-head-option"
                        *ngFor="let option of field.options"
                        (mousedown)="$event.preventDefault()"
                        (click)="selectBudgetHead(field.key, option)">
                        {{ option }}
                      </button>
                    </div>
                  </ng-template>
                </div>
                <input *ngSwitchCase="'checkbox'" type="checkbox" [formControlName]="field.key">
                <div *ngSwitchCase="'file'" class="file-input">
                  <input type="file" (change)="handleFileSelect($event, field.key)">
                  <small *ngIf="form.get(field.key)?.value">{{ form.get(field.key)?.value }}</small>
                </div>
                <div *ngSwitchCase="'item-select'" class="lookup-select">
                  <div class="lookup-filters">
                    <input [placeholder]="'common.searchItem' | t" [(ngModel)]="itemSearch" [ngModelOptions]="{ standalone: true }">
                    <select [(ngModel)]="itemTypeFilter" [ngModelOptions]="{ standalone: true }">
                      <option value="">{{ 'common.allTypes' | t }}</option>
                      <option *ngFor="let itemType of itemTypes" [value]="itemType">{{ itemType | t }}</option>
                    </select>
                  </div>
                  <select [formControlName]="field.key">
                    <option value="">{{ 'common.selectItem' | t }}</option>
                    <option *ngFor="let option of filteredItemOptions" [value]="option.name">{{ itemOptionLabel(option) }}</option>
                  </select>
                </div>
                <input *ngSwitchDefault [type]="field.type" [formControlName]="field.key" [readonly]="field.readonly">
              </ng-container>
              <small class="field-error" *ngIf="field.type !== 'items' && fieldControlInvalid(field.key)">{{ fieldValidationMessage(field.key, field.label) }}</small>
            </label>
          </ng-template>
        </ng-container>
        <div class="form-actions">
          <button type="submit">{{ editingId ? ((isItemDatabase ? 'common.modify' : 'Update') | t) : ((isItemDatabase ? 'common.addItem' : 'Create') | t) }}</button>
          <button type="button" class="secondary" (click)="resetForm()">{{ 'Clear' | t }}</button>
        </div>
      </form>
      <p class="error" *ngIf="error">{{ error }}</p>
    </section>

    <section class="panel">
      <div class="table-toolbar">
        <input [placeholder]="'common.searchRecords' | t" [(ngModel)]="search" (keyup.enter)="searchRecords()">
        <button type="button" class="secondary" (click)="searchRecords()">{{ 'common.search' | t }}</button>
        <button type="button" class="danger" *ngIf="isItemDatabase" [disabled]="selectedCount === 0" (click)="deleteSelected()">
          {{ 'common.deleteSelected' | t }}
        </button>
      </div>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th class="select-column" *ngIf="isItemDatabase">{{ 'common.select' | t }}</th>
              <th *ngFor="let column of columns" [class.wrap-cell]="isWrapColumn(column)">{{ column.label | t }}</th>
              <th>{{ 'common.actions' | t }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of pageResult?.items">
              <td class="select-column" *ngIf="isItemDatabase">
                <input class="row-selector" type="checkbox" [checked]="isSelected(item)" (change)="toggleSelection(item, $event)" [attr.aria-label]="'common.selectItem' | t">
              </td>
              <td *ngFor="let column of columns" [class.wrap-cell]="isWrapColumn(column)">{{ formatColumnValue(item, column) }}</td>
              <td class="actions">
                <button type="button" class="secondary" (click)="view(item)">{{ 'common.view' | t }}</button>
                <button type="button" class="secondary" (click)="edit(item)">{{ isItemDatabase ? ('common.modify' | t) : ('common.edit' | t) }}</button>
                <button type="button" class="danger" (click)="remove(item)">{{ 'common.delete' | t }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <app-pagination
        [total]="totalRecords"
        [page]="page"
        [pageSize]="size"
        [pageSizes]="pageSizes"
        (pageChange)="goToPage($event)"
        (pageSizeChange)="changePageSize($event)">
      </app-pagination>
    </section>

    <section class="modal-backdrop" *ngIf="viewRecord" (click)="closeView()">
      <article class="view-modal" (click)="$event.stopPropagation()">
        <div class="view-modal-header">
          <div>
            <h2>{{ stageLabel | t }} {{ 'lifecycle.details' | t }}</h2>
            <p>{{ 'lifecycle.recordNo' | t }}{{ formatValue(viewRecord['id']) }}</p>
          </div>
          <div class="view-actions">
            <button type="button" class="secondary" (click)="printView()">{{ 'Print' | t }}</button>
            <button type="button" class="secondary" (click)="closeView()">{{ 'Close' | t }}</button>
          </div>
        </div>
        <dl class="view-grid">
          <ng-container *ngFor="let column of viewColumns">
            <dt>{{ column.label | t }}</dt>
            <dd [class.wrap-cell]="isWrapColumn(column)">{{ formatColumnValue(viewRecord, column) }}</dd>
          </ng-container>
        </dl>
      </article>
    </section>
  `
})
export class LifecycleComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(I18nService);
  stage = 'requisitions';
  stageLabel = 'Requisition';
  fields: FieldDef[] = [];
  columns: FieldDef[] = [];
  form = this.fb.group({});
  pageResult?: PageResult;
  page = 1;
  size = 10;
  readonly pageSizes = [10, 25, 50, 100];
  search = '';
  editingId?: number;
  viewRecord?: Record<string, unknown>;
  error = '';
  validationAttempted = false;
  selectedItemIds = new Set<number>();
  itemOptions: ItemMaster[] = [];
  establishments: OFESEstablishment[] = [];
  establishmentOptions: SelectOption[] = [];
  itemTypes: string[] = [];
  itemSearch = '';
  itemTypeFilter = '';
  readonly otherOption = OTHER_OPTION;

  ngOnInit(): void {
    this.route.paramMap.pipe(switchMap((params) => {
      this.stage = params.get('stage') || 'requisitions';
      if (HIDDEN_STAGES.has(this.stage)) {
        this.router.navigateByUrl('/');
        return EMPTY;
      }
      this.stageLabel = STAGE_LABELS[this.stage] || this.stage.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
      this.configureForm();
      return this.api.list(this.stage, this.page, this.size, this.search);
    })).subscribe({
      next: (result) => this.applyPage(result),
      error: (err) => this.error = this.errorMessage(err, 'lifecycle.failedLoadRecords'),
    });
  }

  load(): void {
    this.api.list(this.stage, this.page, this.size, this.search).subscribe({
      next: (result) => this.applyPage(result),
      error: (err) => this.error = this.errorMessage(err, 'lifecycle.failedLoadRecords'),
    });
  }

  searchRecords(): void {
    this.page = 1;
    this.load();
  }

  changePageSize(size: number): void {
    this.size = size;
    this.page = 1;
    this.load();
  }

  goToPage(page: number): void {
    const nextPage = Math.min(Math.max(1, page), this.totalPages);
    if (nextPage === this.page) return;
    this.page = nextPage;
    this.load();
  }

  save(): void {
    this.validationAttempted = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.error = this.i18n.t('lifecycle.validationSummary');
      return;
    }
    const payload = this.cleanPayload(this.form.getRawValue() as Record<string, unknown>);
    if (!this.validateAdministrativeApprovalQuantity(payload)) return;
    const request = this.editingId ? this.api.update(this.stage, this.editingId, payload) : this.api.create(this.stage, payload);
    request.subscribe({
      next: () => {
        this.resetForm();
        this.load();
      },
      error: (err) => this.error = this.errorMessage(err, 'lifecycle.unableSaveRecord')
    });
  }

  edit(item: Record<string, unknown>): void {
    this.validationAttempted = false;
    this.error = '';
    this.editingId = Number(item['id']);
    this.setRequisitionItems(item['requisition_items']);
    this.setTenderItems(item['tender_items']);
    this.setSampleItems(item['sample_items']);
    const formValue = { ...item };
    if (formValue['procurement_mode'] === 'OTHER') {
      formValue['procurement_mode'] = OTHER_OPTION;
    }
    this.form.patchValue(formValue);
    for (const field of this.fields) {
      this.updateOtherFieldValidation(field);
    }
    this.syncEstablishmentDetails(false);
    this.onBudgetHeadChange();
  }

  view(item: Record<string, unknown>): void {
    this.viewRecord = item;
  }

  closeView(): void {
    this.viewRecord = undefined;
  }

  printView(): void {
    if (!this.viewRecord) return;
    const rows = this.viewColumns
      .map((column) => `
        <tr>
          <th>${this.escapeHtml(column.label)}</th>
          <td>${this.escapeHtml(this.formatColumnValue(this.viewRecord || {}, column))}</td>
        </tr>
      `)
      .join('');
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${this.escapeHtml(this.stageLabel)} Details</title>
          <style>
            body { color: #241817; font-family: Arial, sans-serif; margin: 28px; }
            h1 { color: #a61d17; font-size: 22px; margin: 0 0 16px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #cdbba6; padding: 9px 10px; text-align: left; vertical-align: top; }
            th { background: #fff4cc; width: 230px; }
          </style>
        </head>
        <body>
          <h1>${this.escapeHtml(this.stageLabel)} Details</h1>
          <table>${rows}</table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  remove(item: Record<string, unknown>): void {
    const id = Number(item['id']);
    const entity = this.isItemDatabase ? this.i18n.t('common.addItem').toLowerCase() : this.i18n.t('common.record');
    if (!confirm(this.i18n.t('lifecycle.deleteConfirm', { entity, id }))) return;
    this.api.delete(this.stage, id).subscribe({
      next: () => {
        this.selectedItemIds.delete(id);
        this.load();
      },
      error: (err) => this.error = this.errorMessage(err, 'lifecycle.failedDeleteRecord'),
    });
  }

  resetForm(): void {
    this.editingId = undefined;
    this.error = '';
    this.validationAttempted = false;
    this.form.reset();
    for (const field of this.fields.filter((item) => item.type === 'checkbox')) {
      this.form.get(field.key)?.setValue(false, { emitEvent: false });
    }
    this.resetRequisitionItems();
    this.resetTenderItems();
    this.resetSampleItems();
    this.onBudgetHeadChange();
    for (const field of this.fields) {
      this.updateOtherFieldValidation(field);
    }
    this.loadNextRequisitionNo();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.pageResult?.total || 0) / this.size));
  }

  get totalRecords(): number {
    return this.pageResult?.total || 0;
  }

  get pageStart(): number {
    return this.totalRecords ? ((this.page - 1) * this.size) + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min(this.page * this.size, this.totalRecords);
  }

  get isItemDatabase(): boolean {
    return this.stage === 'item-database';
  }

  get selectedCount(): number {
    return this.selectedItemIds.size;
  }

  get requisitionItems(): FormArray<FormGroup> {
    return this.form.get('requisition_items') as unknown as FormArray<FormGroup>;
  }

  get tenderItems(): FormArray<FormGroup> {
    return this.form.get('tender_items') as unknown as FormArray<FormGroup>;
  }

  get sampleItems(): FormArray<FormGroup> {
    return this.form.get('sample_items') as unknown as FormArray<FormGroup>;
  }

  get hasUnserviceableItems(): boolean {
    return !!this.requisitionItems?.controls.some((_, index) => this.isUnserviceable(index));
  }

  get viewColumns(): FieldDef[] {
    return this.columns.filter((column) => column.key !== 'id');
  }

  get filteredItemOptions(): ItemMaster[] {
    const search = this.itemSearch.trim().toLowerCase();
    return this.itemOptions.filter((item) => {
      const matchesType = !this.itemTypeFilter || item.item_type === this.itemTypeFilter;
      const matchesSearch = !search || item.name.toLowerCase().includes(search) || item.item_type.toLowerCase().includes(search);
      return matchesType && matchesSearch;
    });
  }

  itemOptionLabel(item: ItemMaster): string {
    const name = this.i18n.localizeRecord(item as unknown as Record<string, unknown>, 'name');
    const type = this.i18n.localizeRecord(item as unknown as Record<string, unknown>, 'item_type') || this.i18n.t(item.item_type);
    return `${name} (${type})`;
  }

  isSelected(item: Record<string, unknown>): boolean {
    return this.selectedItemIds.has(Number(item['id']));
  }

  toggleSelection(item: Record<string, unknown>, event: Event): void {
    const id = Number(item['id']);
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedItemIds.add(id);
    } else {
      this.selectedItemIds.delete(id);
    }
  }

  deleteSelected(): void {
    const ids = Array.from(this.selectedItemIds);
    if (!ids.length) return;
    if (!confirm(this.i18n.t('lifecycle.deleteSelectedConfirm', { count: ids.length, plural: ids.length === 1 ? '' : 's' }))) return;
    forkJoin(ids.map((id) => this.api.delete(this.stage, id))).subscribe({
      next: () => {
        this.selectedItemIds.clear();
        this.load();
      },
      error: (err) => this.error = this.errorMessage(err, 'lifecycle.failedDeleteSelected'),
    });
  }

  addItem(value: Record<string, unknown> = {}): void {
    if (!this.requisitionItems) return;
    const serialNo = value['serial_no'] || this.requisitionItems.length + 1;
    this.requisitionItems.push(this.createItemGroup({ ...value, serial_no: serialNo }));
  }

  addTenderItem(value: Record<string, unknown> = {}): void {
    if (!this.tenderItems) return;
    const serialNo = value['serial_no'] || this.tenderItems.length + 1;
    this.tenderItems.push(this.createTenderItemGroup({ ...value, serial_no: serialNo }));
  }

  addSampleItem(value: Record<string, unknown> = {}): void {
    if (!this.sampleItems) return;
    const serialNo = value['serial_no'] || this.sampleItems.length + 1;
    this.sampleItems.push(this.createSampleItemGroup({ ...value, serial_no: serialNo }));
  }

  removeItem(index: number): void {
    if (!this.requisitionItems || this.requisitionItems.length <= 1) return;
    this.requisitionItems.removeAt(index);
  }

  downloadRequisitionItems(format: 'csv' | 'xls'): void {
    const headers = [
      'Sr. No.', 'Name of the Item', 'Quantity Required', 'Estimated Cost',
      'Quantity Available at Station/Store', 'Present Condition', 'Condemnation Status',
      'Condemnation Date/Year', 'Justification by Fire Station', 'Remarks', 'Date of Supply',
      'Cause of Unserviceability', 'Unserviceable Remarks'
    ];
    const keys = [
      'serial_no', 'description', 'quantity_required', 'estimated_cost', 'quantity_available',
      'present_condition', 'condemnation_status', 'condemnation_date_year',
      'justification', 'remarks', 'date_of_supply', 'cause_of_unserviceability', 'unserviceable_remarks'
    ];
    const rows = this.requisitionItems.controls.map((control, index) => {
      const value = control.getRawValue() as Record<string, unknown>;
      value['serial_no'] = value['serial_no'] || index + 1;
      value['description'] = value['description'] === OTHER_OPTION ? value['description_other'] : value['description'];
      return keys.map((key) => value[key] ?? '');
    });
    const filename = `requisition-items-${new Date().toISOString().slice(0, 10)}`;
    if (format === 'csv') {
      const csv = [headers, ...rows]
        .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
        .join('\r\n');
      this.downloadText(`\uFEFF${csv}`, 'text/csv;charset=utf-8', `${filename}.csv`);
      return;
    }
    const escape = (value: unknown) => this.escapeHtml(String(value ?? ''));
    const tableRows = [headers, ...rows]
      .map((row, rowIndex) => `<tr>${row.map((value) => `<${rowIndex ? 'td' : 'th'}>${escape(value)}</${rowIndex ? 'td' : 'th'}>`).join('')}</tr>`)
      .join('');
    const workbook = `<html><head><meta charset="utf-8"></head><body><table border="1">${tableRows}</table></body></html>`;
    this.downloadText(`\uFEFF${workbook}`, 'application/vnd.ms-excel;charset=utf-8', `${filename}.xls`);
  }

  private downloadText(content: string, mimeType: string, filename: string): void {
    const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  removeTenderItem(index: number): void {
    if (!this.tenderItems || this.tenderItems.length <= 1) return;
    this.tenderItems.removeAt(index);
  }

  removeSampleItem(index: number): void {
    if (!this.sampleItems || this.sampleItems.length <= 1) return;
    this.sampleItems.removeAt(index);
  }

  syncItemType(index: number): void {
    const control = this.requisitionItems.at(index);
    const description = String(control.get('description')?.value || '').trim().toLowerCase();
    if (!description) {
      control.get('item_type')?.setValue('', { emitEvent: false });
      return;
    }
    const match = this.itemOptions.find((item) => item.name.trim().toLowerCase() === description);
    if (match) {
      control.get('item_type')?.setValue(match.item_type, { emitEvent: false });
    }
  }

  onItemDescriptionChange(index: number): void {
    const control = this.requisitionItems.at(index);
    const descriptionOther = control.get('description_other');
    if (this.isOtherItemDescription(index)) {
      descriptionOther?.setValidators([Validators.required, REQUIRED_TEXT]);
      control.get('item_type')?.setValue('', { emitEvent: false });
      control.get('item_type_other')?.setValue('', { emitEvent: false });
    } else {
      descriptionOther?.clearValidators();
      descriptionOther?.setValue('', { emitEvent: false });
      this.syncItemType(index);
    }
    descriptionOther?.updateValueAndValidity({ emitEvent: false });
    this.onItemTypeChange(index);
  }

  onItemTypeChange(index: number): void {
    const control = this.requisitionItems.at(index);
    const typeOther = control.get('item_type_other');
    if (this.isOtherItemType(index)) {
      typeOther?.setValidators([Validators.required, REQUIRED_TEXT]);
    } else {
      typeOther?.clearValidators();
      typeOther?.setValue('', { emitEvent: false });
    }
    typeOther?.updateValueAndValidity({ emitEvent: false });
  }

  onPresentConditionChange(index: number): void {
    const control = this.requisitionItems.at(index);
    const cause = control.get('cause_of_unserviceability');
    if (this.isUnserviceable(index)) {
      cause?.setValidators([Validators.required, REQUIRED_TEXT]);
    } else {
      cause?.clearValidators();
      control.patchValue({
        date_of_supply: '',
        cause_of_unserviceability: '',
        unserviceable_remarks: ''
      }, { emitEvent: false });
    }
    cause?.updateValueAndValidity({ emitEvent: false });
  }

  isUnserviceable(index: number): boolean {
    return this.requisitionItems.at(index)?.get('present_condition')?.value === 'Unserviceable';
  }

  itemDisplayName(index: number): string {
    const control = this.requisitionItems.at(index);
    return String(control.get('description')?.value === OTHER_OPTION
      ? control.get('description_other')?.value || ''
      : control.get('description')?.value || '');
  }

  isOtherItemDescription(index: number): boolean {
    return this.requisitionItems.at(index)?.get('description')?.value === OTHER_OPTION;
  }

  isOtherItemType(index: number): boolean {
    return this.requisitionItems.at(index)?.get('item_type')?.value === OTHER_OPTION;
  }

  isUnlistedItemDescription(value: unknown): boolean {
    const description = String(value || '').trim();
    return !!description && description !== OTHER_OPTION && !this.itemOptions.some((item) => item.name === description);
  }

  isUnlistedItemType(value: unknown): boolean {
    const itemType = String(value || '').trim();
    return !!itemType && itemType !== OTHER_OPTION && !this.itemTypes.includes(itemType);
  }

  get isOtherBudgetHead(): boolean {
    return this.form.get('budget_head')?.value === OTHER_OPTION;
  }

  onBudgetHeadChange(): void {
    const otherControl = this.form.get('budget_head_other');
    if (!otherControl) return;
    if (this.isOtherBudgetHead) {
      otherControl.setValidators([Validators.required, REQUIRED_TEXT]);
    } else {
      otherControl.clearValidators();
      (otherControl as any).setValue('', { emitEvent: false });
    }
    otherControl.updateValueAndValidity({ emitEvent: false });
  }

  isUnlistedBudgetHead(field: FieldDef): boolean {
    const value = String(this.form.get(field.key)?.value || '').trim();
    return !!value && value !== OTHER_OPTION && !(field.options || []).includes(value);
  }

  fieldControlInvalid(key: string): boolean {
    const control = this.form.get(key);
    return !!control && control.invalid && (control.touched || this.validationAttempted);
  }

  fieldValidationMessage(key: string, label: string): string {
    return this.validationMessage(this.form.get(key)?.errors, label);
  }

  itemControlInvalid(index: number, key: string): boolean {
    const control = this.requisitionItems.at(index)?.get(key);
    return !!control && control.invalid && (control.touched || this.validationAttempted);
  }

  itemValidationMessage(index: number, key: string, label: string): string {
    return this.validationMessage(this.requisitionItems.at(index)?.get(key)?.errors, label);
  }

  private validationMessage(errors: Record<string, unknown> | null | undefined, label: string): string {
    const translatedLabel = this.i18n.t(label);
    if (errors?.['required']) {
      return this.i18n.t('validation.required', { field: translatedLabel });
    }
    if (errors?.['maxlength']) {
      const requiredLength = Number((errors['maxlength'] as Record<string, unknown>)['requiredLength'] || 0);
      return this.i18n.t('validation.maxLength', { field: translatedLabel, length: requiredLength });
    }
    if (errors?.['min']) {
      return this.i18n.t('validation.nonNegative', { field: translatedLabel });
    }
    return this.i18n.t('validation.invalid', { field: translatedLabel });
  }

  syncTenderItemType(index: number): void {
    const control = this.tenderItems.at(index);
    const description = String(control.get('description')?.value || '').trim().toLowerCase();
    if (!description) {
      control.get('item_type')?.setValue('', { emitEvent: false });
      return;
    }
    const match = this.itemOptions.find((item) => item.name.trim().toLowerCase() === description);
    if (match) {
      control.get('item_type')?.setValue(match.item_type, { emitEvent: false });
    }
  }

  handleSelectChange(field: FieldDef): void {
    this.updateOtherFieldValidation(field);
    if (field.lookup === 'establishments') {
      this.syncEstablishmentDetails();
    }
    if (this.stage === 'procurement-cases' && field.key === 'requisition_id') {
      this.syncProcurementCaseFromRequisition();
    }
    if (PROCESS_STAGES.has(this.stage) && field.key === 'procurement_case_id') {
      this.syncProcessFromProcurementCase();
    }
    if (this.stage === 'tenders' && field.key === 'requisition_id') {
      this.syncTenderFromRequisition();
    }
  }

  isProcurementCaseReference(field: FieldDef): boolean {
    return field.referenceStage === 'procurement-cases' && field.key === 'procurement_case_id';
  }

  showsOtherTextBox(field: FieldDef): boolean {
    const value = this.form.get(field.key)?.value;
    return (this.isProcurementCaseReference(field) || field.key === 'procurement_mode') && value === OTHER_OPTION;
  }

  otherFieldKey(field: FieldDef): string {
    return this.isProcurementCaseReference(field) ? 'procurement_case_other' : `${field.key}_other`;
  }

  otherFieldLabel(field: FieldDef): string {
    return this.isProcurementCaseReference(field) ? 'Manual Procurement Case' : 'Other Procurement Mode';
  }

  otherFieldPlaceholder(field: FieldDef): string {
    return this.isProcurementCaseReference(field)
      ? 'Enter procurement case number or reference'
      : 'Enter procurement mode';
  }

  private updateOtherFieldValidation(field: FieldDef): void {
    if (!this.isProcurementCaseReference(field) && field.key !== 'procurement_mode') return;
    const control = this.form.get(this.otherFieldKey(field));
    if (!control) return;
    if (this.showsOtherTextBox(field)) {
      control.setValidators([Validators.required, REQUIRED_TEXT, Validators.maxLength(255)]);
    } else {
      control.clearValidators();
      control.setValue('', { emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  handleFileSelect(event: Event, fieldKey: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.form.get(fieldKey)?.setValue(file.name);
  }

  selectBudgetHead(fieldKey: string, value: string): void {
    this.form.get(fieldKey)?.setValue(value);
  }

  isWrapColumn(column: FieldDef): boolean {
    return [
      'budget_head',
      'gem_procurement_type',
      'delivery_delay_reason',
      'delivery_delay_document_path',
      'remarks',
    ].includes(column.key);
  }

  syncProcurementCaseFromRequisition(): void {
    const requisitionId = Number(this.form.get('requisition_id')?.value || 0);
    if (!requisitionId) return;
    this.api.get('requisitions', requisitionId).subscribe({
      next: (requisition) => this.applyRequisitionToProcurementCase(requisition),
      error: (err) => this.error = this.errorMessage(err, 'Failed to load requisition details.')
    });
  }

  syncProcessFromProcurementCase(): void {
    const procurementCaseId = Number(this.form.get('procurement_case_id')?.value || 0);
    if (!procurementCaseId) return;
    this.api.get('procurement-cases', procurementCaseId).subscribe({
      next: (procurementCase) => {
        this.applyProcurementCaseToProcess(procurementCase);
        const requisitionId = Number(procurementCase['requisition_id'] || 0);
        if (requisitionId) {
          this.api.get('requisitions', requisitionId).subscribe({
            next: (requisition) => this.applyRequisitionToProcess(requisition),
            error: (err) => this.error = this.errorMessage(err, 'Failed to load linked requisition details.')
          });
        }
        this.api.list('tenders', 1, 1000).subscribe({
          next: (result) => {
            const tender = result.items.find((item) => Number(item['procurement_case_id']) === procurementCaseId);
            if (tender) this.applyTenderToProcess(tender);
          },
          error: (err) => this.error = this.errorMessage(err, 'Failed to load linked tender details.')
        });
        this.api.list('purchase-orders', 1, 1000).subscribe({
          next: (result) => {
            const order = result.items.find((item) => Number(item['procurement_case_id']) === procurementCaseId);
            if (order) this.applyPurchaseOrderToProcess(order);
          },
          error: (err) => this.error = this.errorMessage(err, 'Failed to load linked purchase order details.')
        });
      },
      error: (err) => this.error = this.errorMessage(err, 'Failed to load procurement case details.')
    });
  }

  syncTenderFromRequisition(): void {
    const requisitionId = Number(this.form.get('requisition_id')?.value || 0);
    if (!requisitionId) return;
    this.api.get('requisitions', requisitionId).subscribe({
      next: (requisition) => this.applyRequisitionToTender(requisition),
      error: (err) => this.error = this.errorMessage(err, 'Failed to load requisition details.')
    });
  }

  syncEstablishmentDetails(clearMissing = true): void {
    if (this.stage !== 'requisitions') return;
    const receivedFrom = String(this.form.get('received_from')?.value || '').trim();
    const match = this.establishments.find((establishment) =>
      establishment.display_name === receivedFrom ||
      establishment.name === receivedFrom ||
      this.establishmentOptionText(establishment) === receivedFrom
    );
    if (match) {
      this.form.patchValue({
        received_from: this.establishmentOptionText(match),
        received_from_district: match.district,
        received_from_range: match.range_name
      }, { emitEvent: false });
    } else if (clearMissing) {
      this.form.patchValue({
        received_from_district: '',
        received_from_range: ''
      }, { emitEvent: false });
    }
  }

  private configureForm(): void {
    this.fields = STAGE_FIELDS[this.stage] || STAGE_FIELDS['requisitions'];
    const dataFields = this.fields.filter((field) => !field.displayOnly);
    const visibleDataFields = dataFields.filter((field) => !field.hidden);
    this.columns = this.isItemDatabase
      ? visibleDataFields
      : [{ key: 'id', label: 'ID', type: 'number' }, ...visibleDataFields];
    const group: Record<string, any> = {};
    for (const field of dataFields) {
      group[field.key] = field.type === 'items'
        ? this.fb.array([this.createItemGroup()])
        : field.type === 'tender-items'
          ? this.fb.array([this.createTenderItemGroup()])
        : field.type === 'sample-items'
          ? this.fb.array([this.createSampleItemGroup()])
        : field.type === 'checkbox'
          ? [false]
          : ['', this.validatorsForField(field)];
    }
    if (this.stage === 'requisitions') {
      group['budget_head_other'] = [''];
    }
    if (dataFields.some((field) => field.key === 'procurement_mode')) {
      group['procurement_mode_other'] = [''];
    }
    if (dataFields.some((field) => this.isProcurementCaseReference(field))) {
      group['procurement_case_other'] = [''];
    }
    this.form = this.fb.group(group);
    this.loadReferenceOptions();
    this.loadLookupOptions();
    this.loadItemOptions();
    this.loadNextRequisitionNo();
    this.page = 1;
    this.search = '';
    this.editingId = undefined;
    this.selectedItemIds.clear();
  }

  private validatorsForField(field: FieldDef): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (field.required) validators.push(Validators.required);
    if (this.stage === 'requisitions' && field.key === 'requisition_no') {
      validators.push(REQUIRED_TEXT, Validators.maxLength(50));
    }
    if (this.stage === 'requisitions' && field.type === 'number') {
      validators.push(Validators.min(0));
    }
    return validators;
  }

  private applyPage(result: PageResult): void {
    this.pageResult = result;
    const lastPage = Math.max(1, Math.ceil((result.total || 0) / this.size));
    if (this.page > lastPage) {
      this.page = lastPage;
      this.load();
      return;
    }
    const visibleIds = new Set(result.items.map((item) => Number(item['id'])));
    this.selectedItemIds.forEach((id) => {
      if (!visibleIds.has(id)) this.selectedItemIds.delete(id);
    });
    this.error = '';
  }

  private cleanPayload(payload: Record<string, unknown>): Record<string, unknown> {
    const normalized = { ...payload };
    if (normalized['procurement_mode'] === OTHER_OPTION) {
      normalized['procurement_mode'] = 'OTHER';
      normalized['procurement_mode_other'] = String(normalized['procurement_mode_other'] || '').trim();
    } else {
      delete normalized['procurement_mode_other'];
    }
    if (normalized['procurement_case_id'] === OTHER_OPTION) {
      delete normalized['procurement_case_id'];
      normalized['procurement_case_other'] = String(normalized['procurement_case_other'] || '').trim();
    } else {
      delete normalized['procurement_case_other'];
    }
    if (this.stage === 'requisitions') {
      if (normalized['budget_head'] === OTHER_OPTION) {
        normalized['budget_head'] = String(normalized['budget_head_other'] || '').trim();
      }
      delete normalized['budget_head_other'];
    }
    const cleaned = Object.fromEntries(Object.entries(normalized).filter(([, value]) => value !== '' && value !== null));
    if (Array.isArray(cleaned['requisition_items'])) {
      cleaned['requisition_items'] = cleaned['requisition_items']
        .map((item) => item as Record<string, unknown>)
        .map((item) => {
          const normalizedItem = { ...item };
          if (normalizedItem['description'] === OTHER_OPTION) {
            normalizedItem['description'] = String(normalizedItem['description_other'] || '').trim();
          }
          if (normalizedItem['item_type'] === OTHER_OPTION) {
            normalizedItem['item_type'] = String(normalizedItem['item_type_other'] || '').trim();
          }
          normalizedItem['quantity'] = normalizedItem['quantity_required'];
          delete normalizedItem['description_other'];
          delete normalizedItem['item_type_other'];
          return normalizedItem;
        })
        .map((item) => Object.fromEntries(Object.entries(item).filter(([, value]) => value !== '' && value !== null)))
        .filter((item) => Object.entries(item).some(([key, value]) => key !== 'serial_no' && value !== undefined && value !== null && value !== ''))
        .map((item, index) => ({ ...item, serial_no: item['serial_no'] || index + 1 }));
      const firstItem = (cleaned['requisition_items'] as Record<string, unknown>[])[0];
      if (firstItem) {
        cleaned['item_description'] = firstItem['description'];
      }
      const requisitionItems = cleaned['requisition_items'] as Record<string, unknown>[];
      if (requisitionItems.some((item) => item['estimated_cost'] !== undefined)) {
        cleaned['estimated_cost'] = requisitionItems.reduce(
          (total, item) => total + Number(item['estimated_cost'] || 0),
          0
        );
      }
      const totalQuantity = (cleaned['requisition_items'] as Record<string, unknown>[])
        .reduce((total, item) => total + Number(item['quantity'] || 0), 0);
      if (totalQuantity > 0) {
        cleaned['quantity'] = totalQuantity;
      }
    }
    if (Array.isArray(cleaned['tender_items'])) {
      cleaned['tender_items'] = cleaned['tender_items']
        .map((item) => item as Record<string, unknown>)
        .map((item) => Object.fromEntries(Object.entries(item).filter(([, value]) => value !== '' && value !== null)))
        .filter((item) => Object.entries(item).some(([key, value]) => key !== 'serial_no' && value !== undefined && value !== null && value !== ''))
        .map((item, index) => ({ ...item, serial_no: item['serial_no'] || index + 1 }));
      const firstItem = (cleaned['tender_items'] as Record<string, unknown>[])[0];
      if (firstItem) {
        cleaned['item_name'] = firstItem['description'];
      }
      const totalQuantity = (cleaned['tender_items'] as Record<string, unknown>[])
        .reduce((total, item) => total + Number(item['quantity'] || 0), 0);
      if (totalQuantity > 0) {
        cleaned['quantity'] = totalQuantity;
      }
    }
    if (Array.isArray(cleaned['sample_items'])) {
      cleaned['sample_items'] = cleaned['sample_items']
        .map((item) => item as Record<string, unknown>)
        .map((item) => Object.fromEntries(Object.entries(item).filter(([, value]) => value !== '' && value !== null)))
        .filter((item) => Object.entries(item).some(([key, value]) => key !== 'serial_no' && value !== undefined && value !== null && value !== ''))
        .map((item, index) => ({ ...item, serial_no: item['serial_no'] || index + 1 }));
      const sampleItems = cleaned['sample_items'] as Record<string, unknown>[];
      const firstItem = sampleItems[0];
      if (firstItem) {
        const makeModel = firstItem['make_model'] || [firstItem['make'], firstItem['model']].filter(Boolean).join(' ');
        cleaned['item_name'] = firstItem['description'];
        cleaned['make'] = firstItem['make'];
        cleaned['model'] = firstItem['model'];
        cleaned['make_model'] = makeModel;
        cleaned['sample_description'] = firstItem['description'];
      }
      const totalQuantity = sampleItems.reduce((total, item) => total + Number(item['quantity'] || 0), 0);
      if (totalQuantity > 0) {
        cleaned['quantity'] = totalQuantity;
      }
    }
    return cleaned;
  }

  private validateAdministrativeApprovalQuantity(payload: Record<string, unknown>): boolean {
    if (this.stage !== 'requisitions') return true;
    const approvalQuantity = Number(payload['administrative_approval_quantity'] || 0);
    if (approvalQuantity <= 0) return true;
    const requisitionQuantity = this.requisitionQuantity(payload);
    if (approvalQuantity <= requisitionQuantity) return true;
    this.error = this.i18n.t('lifecycle.adminApprovalError');
    return false;
  }

  private requisitionQuantity(payload: Record<string, unknown>): number {
    const items = payload['requisition_items'];
    if (Array.isArray(items)) {
      return items.reduce((total, item) => {
        const row = item as Record<string, unknown>;
        return total + Number(row['quantity_required'] ?? row['quantity'] ?? 0);
      }, 0);
    }
    return Number(payload['quantity'] || 0);
  }

  formatValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return '-';
    if (Array.isArray(value)) {
      const text = value
        .map((item, index) => {
          const row = item as Record<string, unknown>;
          const serialNo = row['serial_no'] || index + 1;
          const quantity = row['quantity'] ? ` (${row['quantity']}${row['unit'] ? ` ${row['unit']}` : ''})` : '';
          const makeModelValue = row['make_model'] || [row['make'], row['model']].filter(Boolean).join(' ');
          const makeModel = makeModelValue ? ` - ${makeModelValue}` : '';
          return `${serialNo}. ${row['description'] || ''}${makeModel}${quantity}`;
        })
        .join('; ');
      return this.i18n.localizeText(text);
    }
    return this.i18n.localizeText(String(value));
  }

  formatColumnValue(item: Record<string, unknown>, column: FieldDef): string {
    if (column.key === 'procurement_mode' && item['procurement_mode'] === 'OTHER') {
      return this.formatValue(item['procurement_mode_other'] || 'Others');
    }
    if (column.key === 'tender_items' && (!Array.isArray(item['tender_items']) || !item['tender_items'].length)) {
      const legacyItem = item['item_name'];
      if (!legacyItem) return '-';
      const quantity = item['quantity'] ? ` (${item['quantity']})` : '';
      return this.i18n.localizeText(`1. ${legacyItem}${quantity}`);
    }
    if (column.key === 'sample_items' && (!Array.isArray(item['sample_items']) || !item['sample_items'].length)) {
      const legacyItem = item['item_name'] || item['sample_description'];
      if (!legacyItem) return '-';
      const makeModel = item['make_model'] || [item['make'], item['model']].filter(Boolean).join(' ');
      const quantity = item['quantity'] ? ` (${item['quantity']})` : '';
      return this.i18n.localizeText(`1. ${legacyItem}${makeModel ? ` - ${makeModel}` : ''}${quantity}`);
    }
    return this.formatValue(item[column.key]);
  }

  private createItemGroup(value: Record<string, unknown> = {}): FormGroup {
    const description = value['description'] || '';
    const itemType = value['item_type'] || this.itemTypeForDescription(value['description']) || '';
    const presentCondition = value['present_condition'] || '';
    const legacyCondemnationStatus = String(value['condemnation_status'] || '');
    const condemnationStatus = ['Pending', 'Under Process', 'Condemned'].includes(legacyCondemnationStatus)
      ? 'Condemnable'
      : legacyCondemnationStatus === 'Not Applicable' ? 'Not Condemnable' : legacyCondemnationStatus;
    return this.fb.group({
      serial_no: [value['serial_no'] || '', Validators.min(1)],
      description: [description, Validators.required],
      description_other: [value['description_other'] || '', description === OTHER_OPTION ? [Validators.required, REQUIRED_TEXT] : []],
      item_type: [itemType],
      item_type_other: [value['item_type_other'] || '', itemType === OTHER_OPTION ? [Validators.required, REQUIRED_TEXT] : []],
      quantity: [value['quantity'] || value['quantity_required'] || '', Validators.min(0)],
      challan_number: [value['challan_number'] || ''],
      challan_date: [value['challan_date'] || ''],
      quantity_required: [value['quantity_required'] ?? value['quantity'] ?? '', Validators.min(0)],
      quantity_available: [value['quantity_available'] ?? '', Validators.min(0)],
      present_condition: [presentCondition],
      condemnation_status: [condemnationStatus],
      condemnation_date_year: [value['condemnation_date_year'] || ''],
      justification: [value['justification'] || ''],
      remarks: [value['remarks'] || ''],
      date_of_supply: [value['date_of_supply'] || ''],
      cause_of_unserviceability: [value['cause_of_unserviceability'] || '', presentCondition === 'Unserviceable' ? [Validators.required, REQUIRED_TEXT] : []],
      unserviceable_remarks: [value['unserviceable_remarks'] || ''],
      unit: [value['unit'] || ''],
      estimated_cost: [value['estimated_cost'] || '', Validators.min(0)]
    });
  }

  private createTenderItemGroup(value: Record<string, unknown> = {}): FormGroup {
    return this.fb.group({
      serial_no: [value['serial_no'] || ''],
      description: [value['description'] || '', Validators.required],
      item_type: [value['item_type'] || this.itemTypeForDescription(value['description']) || ''],
      quantity: [value['quantity'] || ''],
      unit: [value['unit'] || '']
    });
  }

  private resetRequisitionItems(): void {
    if (!this.requisitionItems) return;
    this.requisitionItems.clear();
    this.requisitionItems.push(this.createItemGroup({ serial_no: 1 }));
  }

  private resetTenderItems(): void {
    if (!this.tenderItems) return;
    this.tenderItems.clear();
    this.tenderItems.push(this.createTenderItemGroup({ serial_no: 1 }));
  }

  private resetSampleItems(): void {
    if (!this.sampleItems) return;
    this.sampleItems.clear();
    this.sampleItems.push(this.createSampleItemGroup({ serial_no: 1 }));
  }

  private setRequisitionItems(value: unknown): void {
    if (!this.requisitionItems) return;
    this.requisitionItems.clear();
    const items = Array.isArray(value) && value.length ? value : [{}];
    for (const [index, item] of items.entries()) {
      const row = item as Record<string, unknown>;
      this.requisitionItems.push(this.createItemGroup({ ...row, serial_no: row['serial_no'] || index + 1 }));
    }
  }

  private setTenderItems(value: unknown): void {
    if (!this.tenderItems) return;
    this.tenderItems.clear();
    const items = Array.isArray(value) && value.length ? value : [{}];
    for (const [index, item] of items.entries()) {
      const row = item as Record<string, unknown>;
      this.tenderItems.push(this.createTenderItemGroup({ ...row, serial_no: row['serial_no'] || index + 1 }));
    }
  }

  private setSampleItems(value: unknown): void {
    if (!this.sampleItems) return;
    this.sampleItems.clear();
    const items = Array.isArray(value) && value.length ? value : [{}];
    for (const [index, item] of items.entries()) {
      const row = item as Record<string, unknown>;
      this.sampleItems.push(this.createSampleItemGroup({ ...row, serial_no: row['serial_no'] || index + 1 }));
    }
  }

  private createSampleItemGroup(value: Record<string, unknown> = {}): FormGroup {
    return this.fb.group({
      serial_no: [value['serial_no'] || ''],
      description: [value['description'] || '', Validators.required],
      make: [value['make'] || value['make_model'] || ''],
      model: [value['model'] || ''],
      quantity: [value['quantity'] || ''],
      unit: [value['unit'] || '']
    });
  }

  private itemTypeForDescription(value: unknown): string {
    const description = String(value || '').trim().toLowerCase();
    if (!description) return '';
    return this.itemOptions.find((item) => item.name.trim().toLowerCase() === description)?.item_type || '';
  }

  private patchBlank(values: Record<string, unknown>): void {
    const patch: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(values)) {
      const control = this.form.get(key);
      if (!control || value === undefined || value === null || value === '') continue;
      const current = control.value;
      if (current === undefined || current === null || current === '') {
        patch[key] = value;
      }
    }
    if (Object.keys(patch).length) {
      this.form.patchValue(patch, { emitEvent: false });
      for (const field of this.fields) {
        this.updateOtherFieldValidation(field);
      }
    }
  }

  private procurementModeDefaults(mode: unknown): { emd_applicable: boolean; pbg_applicable: boolean; bid_type: string } {
    const normalized = String(mode || '').trim().toUpperCase();
    if (normalized === 'E_TENDER' || normalized === 'GLOBAL_TENDER') {
      return { emd_applicable: true, pbg_applicable: true, bid_type: normalized === 'E_TENDER' ? 'Odisha e-Procurement' : 'Global Tender' };
    }
    if (normalized === 'GEM') {
      return { emd_applicable: false, pbg_applicable: true, bid_type: 'GeM Procurement' };
    }
    if (normalized === 'LPC') {
      return { emd_applicable: false, pbg_applicable: false, bid_type: 'Local Purchase Committee' };
    }
    if (normalized === 'OPEN_MARKET') {
      return { emd_applicable: false, pbg_applicable: false, bid_type: 'Open Market' };
    }
    return { emd_applicable: false, pbg_applicable: false, bid_type: 'Open Tender' };
  }

  private totalQuantity(items: Record<string, unknown>[], fallback: unknown = ''): unknown {
    const total = items.reduce((sum, item) => sum + Number(item['quantity'] || 0), 0);
    return total || fallback;
  }

  private requisitionItemsForTender(requisition: Record<string, unknown>): Record<string, unknown>[] {
    const requisitionItems = Array.isArray(requisition['requisition_items'])
      ? requisition['requisition_items'] as Record<string, unknown>[]
      : [];
    if (requisitionItems.length) {
      return requisitionItems.map((item, index) => ({
        serial_no: item['serial_no'] || index + 1,
        description: item['description'],
        item_type: item['item_type'],
        quantity: item['quantity'],
        unit: item['unit']
      }));
    }
    return [{
      serial_no: 1,
      description: requisition['item_description'],
      quantity: requisition['quantity']
    }];
  }

  private applyRequisitionToProcurementCase(requisition: Record<string, unknown>): void {
    const modeDefaults = this.procurementModeDefaults(requisition['procurement_mode']);
    this.patchBlank({
      approved_quantity: requisition['administrative_approval_quantity'] || requisition['quantity'],
      approved_estimated_cost: requisition['estimated_cost'],
      procurement_mode: requisition['procurement_mode'] === 'OTHER' ? OTHER_OPTION : requisition['procurement_mode'],
      procurement_mode_other: requisition['procurement_mode_other'],
      case_status: 'In Tendering Process',
      mode_selection_date: new Date().toISOString().slice(0, 10),
      emd_applicable: modeDefaults.emd_applicable,
      pbg_applicable: modeDefaults.pbg_applicable
    });
  }

  private applyProcurementCaseToProcess(procurementCase: Record<string, unknown>): void {
    const caseNo = String(procurementCase['procurement_case_no'] || '');
    const modeDefaults = this.procurementModeDefaults(procurementCase['procurement_mode']);
    const tenderNo = caseNo ? caseNo.replace(/^PC-/, 'TND-') : '';
    const poNo = caseNo ? caseNo.replace(/^PC-/, 'PO-') : '';
    const amount = Number(procurementCase['approved_estimated_cost'] || 0);

    this.patchBlank({
      tender_no: tenderNo,
      tender_date: procurementCase['mode_selection_date'],
      requisition_id: procurementCase['requisition_id'],
      title: caseNo,
      estimated_cost: procurementCase['approved_estimated_cost'],
      quantity: procurementCase['approved_quantity'],
      bid_type: modeDefaults.bid_type,
      amount: this.stage === 'tenders'
        ? procurementCase['approved_estimated_cost']
        : this.stage === 'emds' && modeDefaults.emd_applicable
          ? Math.round(amount * 0.02 * 100) / 100
          : '',
      po_no: poNo,
      po_date: procurementCase['sanction_date'],
      procurement_mode: procurementCase['procurement_mode'] === 'OTHER' ? OTHER_OPTION : procurementCase['procurement_mode'],
      procurement_mode_other: procurementCase['procurement_mode_other'],
      tender_reference_no: tenderNo || caseNo,
      tender_reference_date: procurementCase['mode_selection_date'],
      process_initiate_date: procurementCase['mode_selection_date'],
      po_value: procurementCase['approved_estimated_cost'],
      emd_unique_id: caseNo ? `EMD-${caseNo}` : '',
      entry_date: new Date().toISOString().slice(0, 10),
      emd_form: modeDefaults.emd_applicable ? 'BG/FDR' : '',
      contract_number: poNo || caseNo,
      contract_date: procurementCase['sanction_date'],
      security_form: modeDefaults.pbg_applicable ? 'BG/FDR' : '',
      payment_status: 'Pending',
      refund_status: 'Pending',
      release_status: 'Held'
    });
  }

  private applyRequisitionToProcess(requisition: Record<string, unknown>): void {
    const tenderItems = this.requisitionItemsForTender(requisition);
    const firstItem = tenderItems[0];
    if (this.stage === 'tenders') {
      this.setTenderItems(tenderItems);
    }
    if (this.stage === 'samples') {
      this.setSampleItems(tenderItems);
    }
    this.patchBlank({
      tender_items: tenderItems,
      sample_items: tenderItems,
      item_name: firstItem?.['description'] || requisition['item_description'],
      item_description: firstItem?.['description'] || requisition['item_description'],
      sample_description: firstItem?.['description'] || requisition['item_description'],
      quantity: this.totalQuantity(tenderItems, requisition['quantity']),
      accepted_quantity: this.totalQuantity(tenderItems, requisition['quantity']),
      estimated_cost: requisition['estimated_cost'],
      budget_head: requisition['budget_head']
    });
  }

  private applyTenderToProcess(tender: Record<string, unknown>): void {
    this.patchBlank({
      tender_id: tender['id'],
      tender_number: tender['tender_no'],
      tender_date: tender['tender_date'],
      item_name: tender['item_name'],
      item_description: tender['item_name'],
      tender_reference_no: tender['tender_no'],
      tender_reference_date: tender['tender_date'],
      quantity: tender['quantity'],
      estimated_cost: tender['estimated_cost'],
      quoted_amount: tender['estimated_cost'],
      invoice_amount: tender['estimated_cost']
    });
  }

  private applyPurchaseOrderToProcess(order: Record<string, unknown>): void {
    const value = Number(order['po_value'] || order['estimated_cost'] || 0);
    this.patchBlank({
      po_id: order['id'],
      contract_number: order['po_no'],
      contract_date: order['po_date'],
      item_name: order['item_description'],
      item_description: order['item_description'],
      quantity: order['quantity'],
      accepted_quantity: order['quantity'],
      vendor_name: order['vendor_name'],
      bidder_name: order['vendor_name'],
      invoice_amount: order['po_value'] || order['estimated_cost'],
      amount: value ? Math.round(value * 0.1 * 100) / 100 : ''
    });
  }

  private applyRequisitionToTender(requisition: Record<string, unknown>): void {
    const tenderItems = this.requisitionItemsForTender(requisition);
    this.setTenderItems(tenderItems);
    this.form.patchValue({
      quantity: this.totalQuantity(tenderItems, requisition['quantity']) || '',
      estimated_cost: requisition['estimated_cost'] || '',
      budget_head: requisition['budget_head'] || ''
    } as Record<string, unknown>);
  }

  private loadReferenceOptions(): void {
    for (const field of this.fields.filter((item) => item.referenceStage)) {
      field.referenceOptions = [];
      this.api.list(field.referenceStage!, 1, 1000).subscribe({
        next: (result) => {
          const items = field.referenceMode
            ? result.items.filter((item) => String(item['procurement_mode'] || '') === field.referenceMode)
            : result.items;
          field.referenceOptions = items.map((item) => ({
            value: Number(item['id']),
            label: this.referenceLabel(item, field)
          }));
        },
        error: (err) => this.error = this.errorMessage(err, `Failed to load ${this.i18n.t(field.label)} options.`)
      });
    }
  }

  private loadLookupOptions(): void {
    const budgetHeadFields = this.fields.filter((field) => field.lookup === 'budget-heads');
    if (budgetHeadFields.length) {
      this.api.budgetHeads().subscribe({
        next: (budgetHeads) => {
          const options = budgetHeads.map((budgetHead) => this.i18n.localizeRecord(budgetHead as unknown as Record<string, unknown>, 'display_name'));
          for (const field of budgetHeadFields) {
            field.options = options;
          }
        },
        error: (err) => this.error = this.errorMessage(err, 'Failed to load budget head options.')
      });
    }

    const establishmentFields = this.fields.filter((field) => field.lookup === 'establishments');
    if (establishmentFields.length) {
      this.api.establishments().subscribe({
        next: (establishments) => {
          this.establishments = establishments;
          const optionItems = establishments.map((establishment) => ({
            value: this.establishmentOptionText(establishment),
            label: this.establishmentOptionText(establishment)
          }));
          this.establishmentOptions = optionItems;
          for (const field of establishmentFields) {
            field.optionItems = optionItems;
          }
          this.syncEstablishmentDetails(false);
        },
        error: (err) => this.error = this.errorMessage(err, 'Failed to load establishment options.')
      });
    }
  }

  private establishmentOptionText(establishment: OFESEstablishment): string {
    const name = this.i18n.localizeRecord(establishment as unknown as Record<string, unknown>, 'name');
    const range = this.i18n.localizeRecord(establishment as unknown as Record<string, unknown>, 'range_name');
    return `${name} - ${range}`;
  }

  private loadItemOptions(): void {
    if (!this.fields.some((field) => field.type === 'item-select' || field.type === 'items' || field.type === 'tender-items' || field.type === 'sample-items')) return;
    this.api.itemMaster().subscribe({
      next: (items) => this.itemOptions = items,
      error: (err) => this.error = this.errorMessage(err, 'Failed to load item options.')
    });
    this.api.itemTypes().subscribe({
      next: (types) => this.itemTypes = types,
      error: (err) => this.error = this.errorMessage(err, 'Failed to load item type filters.')
    });
  }

  private loadNextRequisitionNo(): void {
    if (this.stage !== 'requisitions' || this.editingId) return;
    this.api.nextRequisitionNo().subscribe({
      next: (result) => {
        if (this.stage === 'requisitions' && !this.editingId) {
          this.form.patchValue({ requisition_no: result.requisition_no } as Record<string, unknown>);
        }
      },
      error: (err) => this.error = this.errorMessage(err, 'Failed to generate requisition number.')
    });
  }

  private referenceLabel(item: Record<string, unknown>, field: FieldDef): string {
    const values = (field.referenceLabelKeys || [])
      .map((key) => item[key])
      .filter((value) => value !== undefined && value !== null && value !== '')
      .map(String);
    return [`#${item['id']}`, ...values].join(' - ');
  }

  private errorMessage(err: any, fallback: string): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string') return this.i18n.t(detail);
    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          const location = Array.isArray(item?.loc) ? item.loc.join('.') : undefined;
          return [location, item?.msg].filter(Boolean).join(': ');
        })
        .filter(Boolean)
        .join(' ');
    }
    return this.i18n.t(fallback);
  }

  private escapeHtml(value: unknown): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
